import { __extends } from "tslib";
import { alCoerceNumber, alCoerceString, alDefineObjectProperties, alForEachProperty, alInstanceOf, alIsIndex, alToBoolean, alToInteger, alToString } from '../runtime';
import { Debug, release, isNullOrUndefined } from '@awayfl/swf-loader';
import { loadAVM1DataObject } from './AVM1LoadVars';
import { avm1BroadcastEvent } from './AVM1Utils';
import { XMLNode } from '../customAway/xml/XMLNode';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
import { AVM1PropertyDescriptor } from '../runtime/AVM1PropertyDescriptor';
var AVM1XMLNodeType;
(function (AVM1XMLNodeType) {
    AVM1XMLNodeType[AVM1XMLNodeType["ELEMENT_NODE"] = 1] = "ELEMENT_NODE";
    AVM1XMLNodeType[AVM1XMLNodeType["TEXT_NODE"] = 3] = "TEXT_NODE";
})(AVM1XMLNodeType || (AVM1XMLNodeType = {}));
function toAS3XMLNode(as2Node) {
    if (!(as2Node instanceof AVM1Object)) {
        return null;
    }
    var context = as2Node.context;
    if (!alInstanceOf(context, as2Node, context.globals.XMLNode)) {
        return null;
    }
    release || Debug.assert(as2Node.as3XMLNode);
    return as2Node.as3XMLNode;
}
function fromAS3XMLNode(context, as3Node) {
    if (isNullOrUndefined(as3Node)) {
        return undefined;
    }
    var as2Node = as3Node._as2Node;
    if (!as2Node) {
        as2Node = new AVM1Object(context);
        as2Node.alPrototype = context.globals.XMLNode.alGetPrototypeProperty();
        AVM1XMLNodePrototype.prototype.initializeFromAS3Node.call(as2Node, as3Node);
    }
    else {
        release || Debug.assert(as2Node.context === context);
    }
    return as2Node;
}
var AVM1XMLNodeFunction = /** @class */ (function (_super) {
    __extends(AVM1XMLNodeFunction, _super);
    function AVM1XMLNodeFunction(context) {
        var _this = _super.call(this, context) || this;
        _this.alSetOwnPrototypeProperty(new AVM1XMLNodePrototype(context, _this));
        return _this;
    }
    AVM1XMLNodeFunction.prototype.alConstruct = function (args) {
        if (!args && args.length < 2) {
            Debug.notImplemented('Unsupported amount of parameters for AVM1XMLNode constructor');
            return undefined;
        }
        var type = alCoerceNumber(this.context, args[0]);
        var value = alCoerceString(this.context, args[1]);
        if (type !== AVM1XMLNodeType.ELEMENT_NODE && type !== AVM1XMLNodeType.TEXT_NODE) {
            Debug.notImplemented('Unsupported AVM1XMLNode type: ' + type);
            return undefined;
        }
        var obj = new AVM1Object(this.context);
        obj.alPrototype = this.alGetPrototypeProperty();
        AVM1XMLNodePrototype.prototype.initializeNode.call(obj, type, value);
        return obj;
    };
    AVM1XMLNodeFunction.prototype.alCall = function (thisArg, args) {
        return this.alConstruct(args);
    };
    return AVM1XMLNodeFunction;
}(AVM1Function));
export { AVM1XMLNodeFunction };
var AVM1XMLNodeChildNodes = /** @class */ (function (_super) {
    __extends(AVM1XMLNodeChildNodes, _super);
    function AVM1XMLNodeChildNodes(context, as3XMLNode) {
        var _this = _super.call(this, context) || this;
        _this._as3XMLNode = as3XMLNode;
        _this._cachedNodePropertyDescriptor = new AVM1PropertyDescriptor(64 /* DATA */ |
            2 /* DONT_DELETE */ |
            4 /* READ_ONLY */, undefined);
        alDefineObjectProperties(_this, {
            length: {
                get: _this.getLength
            }
        });
        return _this;
    }
    Object.defineProperty(AVM1XMLNodeChildNodes.prototype, "as3ChildNodes", {
        get: function () {
            return this._as3XMLNode.childNodes; //axGetPublicProperty('childNodes').value; // TODO .childNodes
        },
        enumerable: true,
        configurable: true
    });
    AVM1XMLNodeChildNodes.prototype.getLength = function () {
        return this.as3ChildNodes.length;
    };
    AVM1XMLNodeChildNodes.prototype.alGetOwnProperty = function (p) {
        if (alIsIndex(this.context, p)) {
            var index = alToInteger(this.context, p);
            if (index >= 0 && index < this.as3ChildNodes.length) {
                this._cachedNodePropertyDescriptor.value = fromAS3XMLNode(this.context, this.as3ChildNodes[index]);
                return this._cachedNodePropertyDescriptor;
            }
        }
        return _super.prototype.alGetOwnProperty.call(this, p);
    };
    return AVM1XMLNodeChildNodes;
}(AVM1Object));
var AVM1XMLNodeAttributes = /** @class */ (function (_super) {
    __extends(AVM1XMLNodeAttributes, _super);
    function AVM1XMLNodeAttributes(context, as3Attributes) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.builtins.Object.alGetPrototypeProperty();
        _this._nativeAttr = as3Attributes;
        _this._cachedNodePropertyDescriptor = new AVM1PropertyDescriptor(64 /* DATA */, undefined);
        return _this;
    }
    AVM1XMLNodeAttributes.prototype.alGetOwnProperty = function (p) {
        var name = alCoerceString(this.context, p);
        var data = this._nativeAttr.getNamedItem(name);
        if (data) {
            this._cachedNodePropertyDescriptor.value = data.nodeValue;
            return this._cachedNodePropertyDescriptor;
        }
        return void 0;
    };
    AVM1XMLNodeAttributes.prototype.alSetOwnProperty = function (p, desc) {
        var name = alCoerceString(this.context, p);
        if ((desc.flags & 64 /* DATA */)) {
            var value = alCoerceString(this.context, desc.value);
            // this can be unsafe, for NodeNamedMap we should use setNamedItem
            this._nativeAttr[name] = value;
        }
    };
    AVM1XMLNodeAttributes.prototype.alHasOwnProperty = function (p) {
        var name = alCoerceString(this.context, p);
        return !!this._nativeAttr.getNamedItem(name);
    };
    AVM1XMLNodeAttributes.prototype.alDeleteOwnProperty = function (p) {
        var name = alCoerceString(this.context, p);
        this._nativeAttr.removeNamedItem(name);
    };
    AVM1XMLNodeAttributes.prototype.alGetOwnPropertiesKeys = function () {
        var names = [];
        for (var i = 0; i < this._nativeAttr.length; i++) {
            names[i] = alCoerceString(this.context, this._nativeAttr.item(i).name);
        }
        return names;
    };
    return AVM1XMLNodeAttributes;
}(AVM1Object));
var AVM1XMLNodePrototype = /** @class */ (function (_super) {
    __extends(AVM1XMLNodePrototype, _super);
    function AVM1XMLNodePrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            attributes: {
                get: _this.getAttributes,
                set: _this.setAttributes
            },
            childNodes: {
                get: _this.getChildNodes
            },
            firstChild: {
                get: _this.getFirstChild
            },
            lastChild: {
                get: _this.getLastChild
            },
            localName: {
                get: _this.getLocalName
            },
            namespaceURI: {
                get: _this.getNamespaceURI
            },
            nextSibling: {
                get: _this.getNextSibling
            },
            nodeName: {
                get: _this.getNodeName,
                set: _this.setNodeName
            },
            nodeType: {
                get: _this.getNodeType
            },
            nodeValue: {
                get: _this.getNodeValue,
                set: _this.setNodeValue
            },
            parentNode: {
                get: _this.getParentNode
            },
            prefix: {
                get: _this.getPrefix
            },
            previousSibling: {
                get: _this.getPreviousSibling
            },
            appendChild: {
                value: _this.appendChild
            },
            cloneNode: {
                value: _this.cloneNode
            },
            getNamespaceForPrefix: {
                value: _this.getNamespaceForPrefix
            },
            getPrefixForNamespace: {
                value: _this.getPrefixForNamespace
            },
            hasChildNodes: {
                value: _this.hasChildNodes
            },
            insertBefore: {
                value: _this.insertBefore
            },
            removeNode: {
                value: _this.removeNode
            },
            toString: {
                value: _this._toString
            }
        });
        return _this;
    }
    AVM1XMLNodePrototype.prototype.initializeNode = function (type, value) {
        this.as3XMLNode = new XMLNode(type, value);
        this._attributes = undefined;
        this._childNodes = undefined;
        AVM1XMLNodePrototype.addMap(this.as3XMLNode, this);
    };
    AVM1XMLNodePrototype.prototype.initializeFromAS3Node = function (as3XMLNode) {
        this.as3XMLNode = as3XMLNode;
        this._attributes = undefined;
        this._childNodes = undefined;
        AVM1XMLNodePrototype.addMap(this.as3XMLNode, this);
    };
    AVM1XMLNodePrototype.prototype._toString = function () {
        //	to match FP output we use replace
        //	to converting "/>" to " />"
        return new XMLSerializer().serializeToString(this.as3XMLNode).replace(/\/>/g, ' />');
    };
    AVM1XMLNodePrototype.prototype.appendChild = function (newChild) {
        this.as3XMLNode.appendChild(toAS3XMLNode(newChild));
    };
    AVM1XMLNodePrototype.prototype.getAttributes = function () {
        var as3Attributes = this.as3XMLNode.attributes;
        if (isNullOrUndefined(as3Attributes)) {
            return undefined;
        }
        // TODO create a proxy to map AVM2 object stuff to AVM1
        if (!this._attributes) {
            this._attributes = new AVM1XMLNodeAttributes(this.context, as3Attributes);
        }
        return this._attributes;
    };
    AVM1XMLNodePrototype.prototype.setAttributes = function (value) {
        var _this = this;
        // this method has unstable implementation, mixed from native and emulative
        if (isNullOrUndefined(value)) {
            this._attributes = undefined;
            return;
        }
        //todo we can\'t set attribute for node easly, `attributes` is read only,
        // for this we MUST iterate over attribute and call `setAttribute`
        if (value instanceof AVM1XMLNodeAttributes) {
            this._attributes = value;
            return;
        }
        var context = this.context;
        var as3Attributes = this.as3XMLNode.attributes;
        alForEachProperty(value, function (prop) {
            var name = alCoerceString(context, prop);
            var value = alCoerceString(context, _this.alGet(prop));
            var attr = /* dom */ document.createAttribute(name);
            attr.value = value;
            as3Attributes.setNamedItem(attr);
        }, this);
        console.warn('[XMLNode] Invalid attribute implementation');
        this._attributes = new AVM1XMLNodeAttributes(context, this.as3XMLNode.attributes);
    };
    AVM1XMLNodePrototype.prototype.getChildNodes = function () {
        if (!this._childNodes) {
            this._childNodes = new AVM1XMLNodeChildNodes(this.context, this.as3XMLNode);
        }
        return this._childNodes;
    };
    AVM1XMLNodePrototype.prototype.cloneNode = function (deepClone) {
        deepClone = alToBoolean(this.context, deepClone);
        var clone = this.as3XMLNode.axCallPublicProperty('cloneNode', [deepClone]);
        return fromAS3XMLNode(this.context, clone);
    };
    AVM1XMLNodePrototype.prototype.getFirstChild = function () {
        return fromAS3XMLNode(this.context, this.as3XMLNode.firstChild);
    };
    AVM1XMLNodePrototype.prototype.getNamespaceForPrefix = function (prefix) {
        return this.as3XMLNode.axCallPublicProperty('getNamespaceForPrefix', [prefix]);
    };
    AVM1XMLNodePrototype.prototype.getPrefixForNamespace = function (nsURI) {
        return this.as3XMLNode.axCallPublicProperty('getNamespaceForPrefix', [nsURI]);
    };
    AVM1XMLNodePrototype.prototype.hasChildNodes = function () {
        return this.as3XMLNode.hasChildNodes();
    };
    AVM1XMLNodePrototype.prototype.insertBefore = function (newChild, insertPoint) {
        this.as3XMLNode.axCallPublicProperty('insertBefore', [toAS3XMLNode(newChild), toAS3XMLNode(insertPoint)]);
    };
    AVM1XMLNodePrototype.prototype.getLastChild = function () {
        return fromAS3XMLNode(this.context, this.as3XMLNode.lastChild);
    };
    AVM1XMLNodePrototype.prototype.getLocalName = function () {
        return this.as3XMLNode.localName;
    };
    AVM1XMLNodePrototype.prototype.getNamespaceURI = function () {
        return this.as3XMLNode.namespaceURI;
    };
    AVM1XMLNodePrototype.prototype.getNextSibling = function () {
        return fromAS3XMLNode(this.context, this.as3XMLNode.nextSibling);
    };
    AVM1XMLNodePrototype.prototype.getNodeName = function () {
        return this.as3XMLNode.nodeName;
    };
    AVM1XMLNodePrototype.prototype.setNodeName = function (value) {
        value = alCoerceString(this.context, value);
        this.as3XMLNode.nodeName = value;
    };
    AVM1XMLNodePrototype.prototype.getNodeType = function () {
        return this.as3XMLNode.nodeType;
    };
    AVM1XMLNodePrototype.prototype.getNodeValue = function () {
        return this.as3XMLNode.nodeValue;
    };
    AVM1XMLNodePrototype.prototype.setNodeValue = function (value) {
        value = alCoerceString(this.context, value);
        this.as3XMLNode.nodeValue = value;
    };
    AVM1XMLNodePrototype.prototype.getParentNode = function () {
        return fromAS3XMLNode(this.context, this.as3XMLNode.parentNode);
    };
    AVM1XMLNodePrototype.prototype.getPrefix = function () {
        return this.as3XMLNode.prefix;
    };
    AVM1XMLNodePrototype.prototype.getPreviousSibling = function () {
        return fromAS3XMLNode(this.context, this.as3XMLNode.previousSibling);
    };
    AVM1XMLNodePrototype.prototype.removeNode = function () {
        this.as3XMLNode.removeNode();
    };
    AVM1XMLNodePrototype.addMap = function (as3Node, as2Node) {
        release || Debug.assert(!as3Node._as2Node);
        as3Node._as2Node = as2Node;
    };
    return AVM1XMLNodePrototype;
}(AVM1Object));
var AVM1XMLFunction = /** @class */ (function (_super) {
    __extends(AVM1XMLFunction, _super);
    function AVM1XMLFunction(context, xmlNodeClass) {
        var _this = _super.call(this, context) || this;
        _this.alSetOwnPrototypeProperty(new AVM1XMLPrototype(context, _this, xmlNodeClass));
        return _this;
    }
    AVM1XMLFunction.prototype.alConstruct = function (args) {
        var text = args && alCoerceString(this.context, args[0]);
        var obj = new AVM1Object(this.context);
        obj.alPrototype = this.alGetPrototypeProperty();
        obj.isAVM1DataObject = true;
        if (!text)
            text = '';
        AVM1XMLPrototype.prototype.initializeDocument.call(obj, text);
        return obj;
    };
    AVM1XMLFunction.prototype.alCall = function (thisArg, args) {
        return this.alConstruct(args);
    };
    return AVM1XMLFunction;
}(AVM1Function));
export { AVM1XMLFunction };
var AVM1XMLPrototype = /** @class */ (function (_super) {
    __extends(AVM1XMLPrototype, _super);
    function AVM1XMLPrototype(context, fn, xmlNodeClass) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = xmlNodeClass.alGetPrototypeProperty();
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            addRequestHeader: {
                value: _this.addRequestHeader
            },
            createElement: {
                value: _this.createElement
            },
            createTextNode: {
                value: _this.createTextNode
            },
            getBytesLoaded: {
                value: _this.getBytesLoaded
            },
            getBytesTotal: {
                value: _this.getBytesTotal
            },
            ignoreWhite: {
                value: false,
                writable: true
            },
            load: {
                value: _this.load
            },
            parseXML: {
                value: _this.parseXML
            },
            send: {
                value: _this.send
            },
            sendAndLoad: {
                value: _this.sendAndLoad
            },
            onData: {
                value: _this.defaultOnData,
                writable: true
            }
        });
        return _this;
    }
    AVM1XMLPrototype.prototype.initializeDocument = function (text) {
        text = alCoerceString(this.context, text) || null;
        // XMLDocument not callable, you must execute DOMParser for it
        var oParser = new DOMParser();
        var as3Doc = oParser.parseFromString(text, 'application/xml');
        AVM1XMLNodePrototype.prototype.initializeFromAS3Node.call(this, as3Doc);
        this.as3XMLDocument = as3Doc;
    };
    AVM1XMLPrototype.prototype.addRequestHeader = function (headers, headerValue) {
        Debug.notImplemented('AVM1XMLPrototype.addRequestHeader');
    };
    AVM1XMLPrototype.prototype.createElement = function (name) {
        name = alCoerceString(this.context, name);
        var as3Node = this.as3XMLDocument.axCallPublicProperty('createElement', [name]);
        return fromAS3XMLNode(this.context, as3Node);
    };
    AVM1XMLPrototype.prototype.createTextNode = function (value) {
        value = alCoerceString(this.context, value);
        var as3Node = this.as3XMLDocument.axCallPublicProperty('createTextNode', [value]);
        return fromAS3XMLNode(this.context, as3Node);
    };
    AVM1XMLPrototype.prototype.getBytesLoaded = function () {
        if (!this._as3Loader) {
            return undefined;
        }
        return this._as3Loader.bytesLoaded;
    };
    AVM1XMLPrototype.prototype.getBytesTotal = function () {
        if (!this._as3Loader) {
            return undefined;
        }
        return this._as3Loader.bytesTotal;
    };
    AVM1XMLPrototype.prototype.load = function (url) {
        url = alCoerceString(this.context, url);
        if (!url) {
            return false;
        }
        loadAVM1DataObject(this.context, url, null, null, null, this);
        return true;
    };
    AVM1XMLPrototype.prototype.defaultOnData = function (src) {
        if (isNullOrUndefined(src)) {
            avm1BroadcastEvent(this.context, this, 'onLoad', [false]);
            return;
        }
        AVM1XMLPrototype.prototype.parseXML.call(this, src);
        this.alPut('loaded', true);
        avm1BroadcastEvent(this.context, this, 'onLoad', [true]);
    };
    AVM1XMLPrototype.prototype.parseXML = function (value) {
        value = alCoerceString(this.context, value);
        var oParser = new DOMParser();
        var as3Doc = oParser.parseFromString('<syntheticRoot>' + value + '</syntheticRoot>', 'application/xml');
        var error = as3Doc.getElementsByTagName('parsererror');
        if (error.length > 0) {
            var critical = as3Doc.childElementCount === 1;
            var errorText = error[0].textContent;
            if (critical) {
                throw '[AVM1XML] Сritical parsing error:' + errorText;
            }
            console.warn('[AVM1XML] Parsing error:', errorText);
            console.groupCollapsed('[XML Source]:');
            console.log(value);
            console.groupEnd();
        }
        this.as3XMLDocument = as3Doc.childNodes[0];
        AVM1XMLNodePrototype.prototype.initializeFromAS3Node.call(this, this.as3XMLDocument);
        /*this.as3XMLDocument.axSetPublicProperty('ignoreWhite',
            alToBoolean(this.context, this.alGet('ignoreWhite')));
        this.as3XMLDocument.axCallPublicProperty('parseXML', [value]);*/
    };
    AVM1XMLPrototype.prototype.send = function (url, target, method) {
        url = alCoerceString(this.context, url);
        target = isNullOrUndefined(target) ? undefined : alCoerceString(this.context, target);
        method = isNullOrUndefined(method) ? undefined : alCoerceString(this.context, method);
        Debug.notImplemented('AVM1XMLPrototype.send');
        return false;
    };
    AVM1XMLPrototype.prototype.sendAndLoad = function (url, resultXML) {
        url = alCoerceString(this.context, url);
        if (!url) {
            return;
        }
        if (!resultXML.isAVM1DataObject) {
            return;
        }
        Debug.somewhatImplemented('AVM1XMLPrototype.send');
        // TODO check content types and test
        var contentType = this.alGet('contentType');
        contentType = isNullOrUndefined(contentType) ? undefined : alCoerceString(this.context, contentType);
        var data = alToString(this.context, this);
        loadAVM1DataObject(this.context, url, 'POST', contentType, data, resultXML);
    };
    return AVM1XMLPrototype;
}(AVM1Object));
